/**
 * Helsingør Flisecenter — storefront product domain (PDP slice 1).
 * Pure derivation + line-item metadata. No DOM.
 *
 * ADR 0001 fulfillment_state precedence (tracked Shopify inventory):
 * 1. is_procured === true  -> special_order
 * 2. inventory_policy === 'continue' -> backorderable
 * 3. inventory_quantity > 0 -> in_stock
 * 4. else -> out_of_stock_non_backorderable
 *
 * Untracked variants (inventory_management !== 'shopify'): same precedence
 * for steps 1–2; step 3 uses Shopify sellability (available) as positive stock proxy.
 *
 * unit_of_sale (ADR: orthogonal to fulfillment / procurement):
 * Derived only from packaging signals — never from is_procured or fulfillment_state.
 * - whole_box: pieces_per_pack > 1 OR legacy packaging heuristic SKU first char === '1'
 * - per_piece: otherwise
 *
 * Phase 2 — quantity / m2 as one entry mode (no parallel purchase engine):
 * waste factor, pack math, and m² cap messaging gates live here; UI only reads DOM and paints.
 */
(function (global) {
    'use strict';

    var FULFILLMENT = {
        SPECIAL_ORDER: 'special_order',
        BACKORDERABLE: 'backorderable',
        IN_STOCK: 'in_stock',
        OUT: 'out_of_stock_non_backorderable',
    };

    function variantAvailableShopify(variant) {
        if (!variant) return false;
        if (typeof variant.available === 'boolean') return variant.available;
        return variant.stock_status === true || String(variant.stock_status) === 'true';
    }

    function parseQty(variant) {
        var q = parseInt(variant && variant.inventory_quantity, 10);
        return Number.isFinite(q) ? q : 0;
    }

    function isProcuredVariant(variant) {
        var mf = variant && variant.metafields;
        return mf && mf.is_procured === true;
    }

    function inventoryPolicyContinue(variant) {
        return String((variant && variant.inventory_policy) || '') === 'continue';
    }

    function isTrackedShopify(variant) {
        return String((variant && variant.inventory_management) || '') === 'shopify';
    }

    /**
     * @param {object|null} variant
     * @returns {string} fulfillment_state enum
     */
    function deriveFulfillmentState(variant) {
        if (!variant) {
            return FULFILLMENT.OUT;
        }
        if (isProcuredVariant(variant)) {
            return FULFILLMENT.SPECIAL_ORDER;
        }
        if (inventoryPolicyContinue(variant)) {
            return FULFILLMENT.BACKORDERABLE;
        }
        if (isTrackedShopify(variant)) {
            return parseQty(variant) > 0 ? FULFILLMENT.IN_STOCK : FULFILLMENT.OUT;
        }
        return variantAvailableShopify(variant) ? FULFILLMENT.IN_STOCK : FULFILLMENT.OUT;
    }

    /** Legacy primary_state strings used by existing DOM branches. */
    function legacyPrimaryState(fulfillmentState) {
        if (!fulfillmentState) return 'unavailable';
        if (fulfillmentState === FULFILLMENT.IN_STOCK) return 'stocked';
        if (fulfillmentState === FULFILLMENT.OUT) return 'out_of_stock';
        return fulfillmentState;
    }

    function skuLeadsWithOne(variant) {
        try {
            return !!(variant && variant.sku && String(variant.sku).charAt(0) === '1');
        } catch (e) {
            return false;
        }
    }

    /**
     * Packaging only (not procurement). whole_box = multi-piece pack or SKU-leading-1 rule.
     */
    function deriveUnitOfSale(variant) {
        if (!variant) return 'per_piece';
        var mf = variant.metafields || {};
        var piecesPack = parseInt(mf.pieces_per_pack, 10) || 0;
        var wholeBoxPackaging = piecesPack > 1 || skuLeadsWithOne(variant);
        return wholeBoxPackaging ? 'whole_box' : 'per_piece';
    }

    function piecesPerM2Valid(mf) {
        var v = mf && mf.pieces_per_square_meter;
        if (v == null || v === '') return false;
        var s = String(v);
        return s !== '0' && s !== '0.0';
    }

    function deriveQuantityMode(variant) {
        if (!variant) return 'standard';
        var mf = variant.metafields || {};
        return piecesPerM2Valid(mf) ? 'm2_calculator' : 'standard';
    }

    /**
     * @param {object} product window.shopify_product or { is_sample_orderable }
     * @returns {'none'|'sample_eligible'}
     */
    function deriveSampleStatusForProduct(product) {
        var p = product || {};
        return p.is_sample_orderable ? 'sample_eligible' : 'none';
    }

    function specialOrderInventoryBranch(variant) {
        if (!variant || !isProcuredVariant(variant)) return null;
        return parseQty(variant) > 0 ? 'in_stock' : 'storage_only';
    }

    /**
     * Backorder messaging: static strip when state is backorderable;
     * dynamic qty>inventory when stocked legacy — now when in_stock + policy continue is impossible;
     * use backorderable + qty > inventory for "oversold" messaging on continue+stock.
     */
    function deriveShowBackorderInfoStatic(fulfillmentState) {
        return fulfillmentState === FULFILLMENT.BACKORDERABLE;
    }

    function deriveHideStandardBuyButtons(fulfillmentState) {
        return (
            fulfillmentState === FULFILLMENT.SPECIAL_ORDER ||
            fulfillmentState === FULFILLMENT.OUT
        );
    }

    /**
     * m2 / quantity: allow exceeding metafield cap when oversell states.
     */
    function allowsOversellBeyondM2Cap(fulfillmentState) {
        return (
            fulfillmentState === FULFILLMENT.BACKORDERABLE ||
            fulfillmentState === FULFILLMENT.SPECIAL_ORDER
        );
    }

    /**
     * Same policy as legacy: block warning when total m² > cap and cap !== 0,
     * only if fulfillment does not allow oversell.
     */
    function shouldBlockM2TotalSquareMeters(fulfillmentState, totalSquareMeters, capSquareMeters) {
        if (allowsOversellBeyondM2Cap(fulfillmentState)) return false;
        var cap = parseFloat(capSquareMeters, 10);
        if (!Number.isFinite(cap) || cap === 0) return false;
        var t = parseFloat(totalSquareMeters, 10);
        if (!Number.isFinite(t)) return false;
        return t > cap;
    }

    /**
     * PDP m² input: apply +10% waste when checkbox on (same as legacy get_square_meters).
     * @param {number|string} rawSquareMeters
     * @param {boolean} wasteChecked
     */
    function applyM2WasteToSquareMeters(rawSquareMeters, wasteChecked) {
        var n = parseFloat(rawSquareMeters, 10);
        if (!Number.isFinite(n)) {
            n = 0;
        }
        if (wasteChecked) {
            return n * 1.1;
        }
        return n;
    }

    function piecesPerM2FromMetafields(mf) {
        if (!mf) {
            return NaN;
        }
        if (mf.pieces_per_square_meter != null && mf.pieces_per_square_meter !== '') {
            return parseFloat(mf.pieces_per_square_meter, 10);
        }
        if (mf.piece_per_square_meter != null && mf.piece_per_square_meter !== '') {
            return parseFloat(mf.piece_per_square_meter, 10);
        }
        return NaN;
    }

    function piecesPerPackFromMetafields(mf) {
        if (!mf || mf.pieces_per_pack == null || mf.pieces_per_pack === '') {
            return NaN;
        }
        return parseFloat(mf.pieces_per_pack, 10);
    }

    /**
     * Pack count for PDP: round up using actual box coverage, min 1.
     * Older variants without box coverage retain the pieces / pack fallback.
     */
    function calculatePacksCeilFromM2Metafields(squareMetersEffective, metafields) {
        var mf = metafields || {};
        var squareMetersPerPack = parseFloat(mf.per_pack_square_meter, 10);
        if (Number.isFinite(squareMetersPerPack) && squareMetersPerPack > 0) {
            var packs = squareMetersEffective / squareMetersPerPack;
            if (!Number.isFinite(packs)) {
                return 1;
            }
            // Ignore only floating-point noise at whole-box boundaries (including waste).
            // A real excess must still round up to the next box.
            var nearestWholePack = Math.round(packs);
            var tolerance = 4 * Number.EPSILON * Math.max(1, Math.abs(packs));
            if (Math.abs(packs - nearestWholePack) <= tolerance) {
                packs = nearestWholePack;
            }
            return Math.max(Math.ceil(packs), 1);
        }
        var ppm = piecesPerM2FromMetafields(mf);
        var ppp = piecesPerPackFromMetafields(mf);
        if (!Number.isFinite(ppm) || !Number.isFinite(ppp) || ppp === 0) {
            return 1;
        }
        var totalPieces = squareMetersEffective * ppm;
        return Math.max(Math.ceil(totalPieces / ppp), 1);
    }

    /**
     * Legacy rounding from m2-price-calculator (string decimal probe) — keep parity with bundled widget.
     */
    function calculatePacksM2LegacyRounding(squareMetersEffective, piecePerSquareMeter, piecesPerPack) {
        var ppmNum = parseFloat(piecePerSquareMeter, 10);
        var pppNum = parseFloat(piecesPerPack, 10);
        if (!Number.isFinite(ppmNum) || !Number.isFinite(pppNum) || pppNum === 0) {
            return 1;
        }
        var totalPieces = squareMetersEffective * ppmNum;
        var packs = totalPieces / pppNum;
        if (packs < 1) {
            packs = 1;
        }
        var s = String(packs);
        if (s.indexOf('.') !== -1) {
            var dec = s.split('.')[1] || '';
            if (dec.substring(0, 2) !== '00') {
                packs = Math.ceil(packs);
            } else {
                packs = Math.round(packs);
            }
        }
        return packs;
    }

    function totalOrderedSquareMetersFromPacks(packs, metafields) {
        var mf = metafields || {};
        var perPack = parseFloat(mf.per_pack_square_meter, 10);
        if (!Number.isFinite(perPack)) {
            return 0;
        }
        return packs * perPack;
    }

    /**
     * Whether to show the m² “exceeds cap” customer message (not raw property).
     * @param {object} [opts]
     * @param {boolean} [opts.requirePositiveTotalStockMetafield] legacy m2 bundle: hide when total_stock metafield is 0
     * @param {*} [opts.totalStockMetafield]
     */
    function shouldShowM2ExceedUserMessage(fulfillmentState, totalSquareMeters, capSquareMeters, opts) {
        if (!shouldBlockM2TotalSquareMeters(fulfillmentState, totalSquareMeters, capSquareMeters)) {
            return false;
        }
        opts = opts || {};
        if (opts.requirePositiveTotalStockMetafield) {
            var rawSt = opts.totalStockMetafield;
            if (rawSt !== undefined && rawSt !== null && rawSt !== '') {
                var st = parseFloat(rawSt, 10);
                if (Number.isFinite(st) && st === 0) {
                    return false;
                }
            }
        }
        return true;
    }

    /**
     * One snapshot for m2 quantity-entry mode (canonical fulfillment + packs + ordered m²).
     * @param {'ceil'|'legacy_m2_round'} packAlgorithm
     */
    function deriveM2QuantitySnapshot(variant, rawSquareMetersInput, wasteChecked, packAlgorithm) {
        var mf = (variant && variant.metafields) || {};
        var sq = applyM2WasteToSquareMeters(rawSquareMetersInput, !!wasteChecked);
        var packs;
        if (packAlgorithm === 'legacy_m2_round') {
            packs = calculatePacksM2LegacyRounding(
                sq,
                piecesPerM2FromMetafields(mf),
                piecesPerPackFromMetafields(mf)
            );
        } else {
            packs = calculatePacksCeilFromM2Metafields(sq, mf);
        }
        var totalOrderedM2 = totalOrderedSquareMetersFromPacks(packs, mf);
        var fs = deriveFulfillmentState(variant);
        return {
            fulfillment_state: fs,
            effective_square_meters: sq,
            packs: packs,
            total_ordered_square_meters: totalOrderedM2,
        };
    }

    function validatePackQuantityAgainstInventory(fulfillmentState, requestedPacks, inventoryPacks) {
        var req = parseInt(requestedPacks, 10) || 0;
        var inv = parseInt(inventoryPacks, 10) || 0;
        if (allowsOversellBeyondM2Cap(fulfillmentState)) {
            return { ok: true, blocked: false, reason: null };
        }
        if (req > inv && inv >= 0) {
            return { ok: false, blocked: true, reason: 'exceeds_inventory' };
        }
        return { ok: true, blocked: false, reason: null };
    }

    var LINE_KEYS = {
        fulfillment: '_fulfillment_state',
    };

    /**
     * Machine-safe line item properties (leading _ hidden in cart-drawer-item / cart-line-item).
     * @param {object} opts
     * @param {string} opts.fulfillment_state
     * @param {object} [opts.extra] customer-visible or other keys merged last
     */
    function buildLineItemProperties(opts) {
        var out = {};
        if (!opts) return out;
        if (opts.fulfillment_state) {
            out[LINE_KEYS.fulfillment] = String(opts.fulfillment_state);
        }
        if (opts.extra && typeof opts.extra === 'object') {
            Object.keys(opts.extra).forEach(function (k) {
                if (Object.prototype.hasOwnProperty.call(opts.extra, k)) {
                    out[k] = opts.extra[k];
                }
            });
        }
        return out;
    }

    /**
     * Sample SKU line: customer-visible variant details only (no machine line keys).
     */
    function buildSampleLineProperties(variant, product, displayProps) {
        return displayProps && typeof displayProps === 'object' ? Object.assign({}, displayProps) : {};
    }

    function deriveMessageFlags(variant, fulfillmentState) {
        return {
            showProcurementMessaging: fulfillmentState === FULFILLMENT.SPECIAL_ORDER,
            showBackorderStatic: deriveShowBackorderInfoStatic(fulfillmentState),
        };
    }

    function deriveStockValidationSummary(variant, fulfillmentState, requestedPacks) {
        var packs = requestedPacks != null ? requestedPacks : parseQty(variant);
        return validatePackQuantityAgainstInventory(fulfillmentState, packs, parseQty(variant));
    }

    /**
     * Full view object for PDP (extends / replaces embedded orderability JSON).
     */
    function deriveOrderabilityView(variant, product) {
        var p = product || {};
        if (!variant) {
            return {
                fulfillment_state: FULFILLMENT.OUT,
                primary_state: 'unavailable',
                uses_m2_calculator: false,
                sample_orderable: !!p.is_sample_orderable,
                sample_status: deriveSampleStatusForProduct(p),
                sold_in_full_packs_only: false,
                has_low_stock_warning: false,
                special_order_inventory_branch: null,
                hide_standard_buy_buttons: true,
                show_backorder_info_static: false,
                unit_of_sale: 'per_piece',
                quantity_mode: 'standard',
                message_flags: {},
            };
        }
        var fs = deriveFulfillmentState(variant);
        var mf = variant.metafields || {};
        return {
            fulfillment_state: fs,
            primary_state: legacyPrimaryState(fs),
            uses_m2_calculator: deriveQuantityMode(variant) === 'm2_calculator',
            sample_orderable: !!p.is_sample_orderable,
            sample_status: deriveSampleStatusForProduct(p),
            sold_in_full_packs_only: deriveUnitOfSale(variant) === 'whole_box',
            has_low_stock_warning: false,
            special_order_inventory_branch: specialOrderInventoryBranch(variant),
            hide_standard_buy_buttons: deriveHideStandardBuyButtons(fs),
            show_backorder_info_static: deriveShowBackorderInfoStatic(fs),
            unit_of_sale: deriveUnitOfSale(variant),
            quantity_mode: deriveQuantityMode(variant),
            message_flags: deriveMessageFlags(variant, fs),
        };
    }

    /**
     * Canonical on-sale from pricing-canonical JSON on each variant (ADR-002).
     * @param {object|null} variant
     * @returns {boolean}
     */
    function isVariantOnSale(variant) {
        if (!variant) {
            return false;
        }
        var cp = variant.canonical_pricing;
        if (cp && typeof cp.display_compare_price_cents === 'number' && typeof cp.display_price_cents === 'number') {
            return cp.display_compare_price_cents > cp.display_price_cents;
        }
        var mf = variant.metafields || {};
        var m2_disc = parseFloat(mf.per_m2_discount_price);
        if (Number.isFinite(m2_disc) && m2_disc > 0) {
            return true;
        }
        var compare_cents = Number(variant.compare_at_price) || 0;
        var price_cents = Number(variant.price) || 0;
        return compare_cents > price_cents;
    }

    /**
     * PDP variant-picker sale indicators per option value (ADR-002 domain).
     * Position 1 (color): any on-sale variant with that color.
     * Positions 2–3: on-sale variants matching selected_color + that option value.
     * @param {object[]} variants normalized shopify_product.variants
     * @param {string} selected_color current Farve / option1 from picker
     * @returns {{ selected_color: string, positions: Object.<string, Object.<string, boolean>>, size_show_tilbud_badge: Object.<string, boolean> }}
     */
    function derivePdpOptionSaleView(variants, selected_color) {
        var view = {
            selected_color: selected_color || '',
            positions: { '1': {}, '2': {}, '3': {} },
            size_show_tilbud_badge: {},
        };
        if (!Array.isArray(variants) || !variants.length) {
            return view;
        }
        variants.forEach(function (variant) {
            if (!isVariantOnSale(variant)) {
                return;
            }
            var color = variant.color ? String(variant.color) : '';
            var surface = variant.surface ? String(variant.surface) : '';
            var size = variant.size ? String(variant.size) : '';
            if (color) {
                view.positions['1'][color] = true;
            }
            if (selected_color && color === selected_color) {
                if (surface) {
                    view.positions['2'][surface] = true;
                }
                if (size) {
                    view.positions['3'][size] = true;
                    view.size_show_tilbud_badge[size] = true;
                }
            }
        });
        return view;
    }

    /**
     * Product cards / Minimog `Product` view: sold-out badge when canonical state is `out_of_stock_non_backorderable`.
     * Merges `product_data.variants` entry when ids match so metafields match PDP-shaped payloads where present.
     */
    global.hfc_is_sold_out_for_product_card = function hfc_is_sold_out_for_product_card(variant, product_data) {
        if (!variant) {
            return true;
        }
        var merged = variant;
        try {
            if (product_data && Array.isArray(product_data.variants)) {
                var found = product_data.variants.find(function (v) {
                    return Number(v.id) === Number(variant.id);
                });
                if (found) {
                    merged = found;
                }
            }
        } catch (e) {
            merged = variant;
        }
        return deriveFulfillmentState(merged) === FULFILLMENT.OUT;
    };

    global.HFCProductDomain = {
        FULFILLMENT: FULFILLMENT,
        LINE_KEYS: LINE_KEYS,
        variantAvailableShopify: variantAvailableShopify,
        deriveFulfillmentState: deriveFulfillmentState,
        legacyPrimaryState: legacyPrimaryState,
        deriveUnitOfSale: deriveUnitOfSale,
        deriveQuantityMode: deriveQuantityMode,
        deriveSampleStatusForProduct: deriveSampleStatusForProduct,
        allowsOversellBeyondM2Cap: allowsOversellBeyondM2Cap,
        shouldBlockM2TotalSquareMeters: shouldBlockM2TotalSquareMeters,
        shouldShowM2ExceedUserMessage: shouldShowM2ExceedUserMessage,
        applyM2WasteToSquareMeters: applyM2WasteToSquareMeters,
        calculatePacksCeilFromM2Metafields: calculatePacksCeilFromM2Metafields,
        calculatePacksM2LegacyRounding: calculatePacksM2LegacyRounding,
        totalOrderedSquareMetersFromPacks: totalOrderedSquareMetersFromPacks,
        deriveM2QuantitySnapshot: deriveM2QuantitySnapshot,
        validatePackQuantityAgainstInventory: validatePackQuantityAgainstInventory,
        buildLineItemProperties: buildLineItemProperties,
        buildSampleLineProperties: buildSampleLineProperties,
        deriveOrderabilityView: deriveOrderabilityView,
        deriveStockValidationSummary: deriveStockValidationSummary,
        isVariantOnSale: isVariantOnSale,
        derivePdpOptionSaleView: derivePdpOptionSaleView,
    };
})(typeof window !== 'undefined' ? window : this);
